import React, { useState, useMemo, useCallback } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Modal, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, typography } from '../theme';
import { useLocale } from '../contexts/LocaleContext';
import { useRTLStyles } from '../hooks/useRTLStyles';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { withAlpha } from '../theme';

export type DateRangePreset = 'today' | '7d' | '30d' | 'custom';

export interface DateRangePickerProps {
  preset: DateRangePreset;
  startDate?: Date;
  endDate?: Date;
  onChange: (preset: DateRangePreset, startDate?: Date, endDate?: Date) => void;
}

const DAY_MS = 24 * 60 * 60 * 1000;

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
}
function endOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
}
function sameDay(a?: Date, b?: Date): boolean {
  if (!a || !b) return false;
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
function buildPresetRange(preset: Exclude<DateRangePreset, 'custom'>): { startDate: Date; endDate: Date } {
  const now = new Date();
  const endDate = endOfDay(now);
  const daysBack = preset === 'today' ? 0 : preset === '7d' ? 6 : 29;
  const startDate = startOfDay(new Date(now.getTime() - daysBack * DAY_MS));
  return { startDate, endDate };
}

/** Builds a 6x7 calendar grid for the given month — leading/trailing cells from
 * adjacent months are included (nulled out visually) so every row has 7 columns. */
function buildMonthGrid(year: number, month: number): (Date | null)[] {
  const firstDay = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const startingWeekday = firstDay.getDay();
  const cells: (Date | null)[] = [];
  for (let i = 0; i < startingWeekday; i++) cells.push(null);
  for (let day = 1; day <= daysInMonth; day++) cells.push(new Date(year, month, day));
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

/**
 * Single popover: preset chips (Today/7d/30d/Custom) + a real tappable calendar-range
 * grid (click a start day, then an end day) — replaces the old pair of typed-text
 * DatePickerField inputs, which read as "manually written date" rather than a proper
 * date-range selector. Renders via Modal so it portal-mounts at the document root on
 * web (same reasoning as ReportsScreen's other pickers) instead of risking the same
 * stacking-context bug the export dropdown had before it was converted to a Modal.
 */
export const DateRangePicker: React.FC<DateRangePickerProps> = ({ preset, startDate, endDate, onChange }) => {
  const styles = useThemedStyles(getStyles);
  const { t } = useLocale();
  const rtl = useRTLStyles();
  const [visible, setVisible] = useState(false);
  const [viewMonth, setViewMonth] = useState(() => startDate || new Date());
  // Tracks the in-progress custom selection while the popover is open — only committed
  // via onChange once both ends are picked, so cancelling (closing without finishing)
  // never mutates the caller's actual filter state.
  const [pendingStart, setPendingStart] = useState<Date | undefined>(startDate);
  const [pendingEnd, setPendingEnd] = useState<Date | undefined>(endDate);

  const open = useCallback(() => {
    setPendingStart(startDate);
    setPendingEnd(endDate);
    setViewMonth(startDate || new Date());
    setVisible(true);
  }, [startDate, endDate]);

  const applyPreset = (p: Exclude<DateRangePreset, 'custom'>) => {
    const range = buildPresetRange(p);
    onChange(p, range.startDate, range.endDate);
    setVisible(false);
  };

  const handleDayPress = (day: Date) => {
    if (!pendingStart || (pendingStart && pendingEnd)) {
      // Starting a fresh selection.
      setPendingStart(startOfDay(day));
      setPendingEnd(undefined);
      return;
    }
    // Second tap: order start/end regardless of click order (tapping an earlier date
    // second just swaps which end it becomes, rather than producing an inverted range).
    if (day.getTime() < pendingStart.getTime()) {
      setPendingEnd(endOfDay(pendingStart));
      setPendingStart(startOfDay(day));
    } else {
      setPendingEnd(endOfDay(day));
    }
  };

  const confirmCustom = () => {
    if (!pendingStart) return;
    onChange('custom', pendingStart, pendingEnd || endOfDay(pendingStart));
    setVisible(false);
  };

  const grid = useMemo(() => buildMonthGrid(viewMonth.getFullYear(), viewMonth.getMonth()), [viewMonth]);
  const monthLabel = viewMonth.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  const weekdayLabels = useMemo(() => {
    // Locale-aware short weekday names starting Sunday, matching buildMonthGrid's
    // getDay()-based (Sunday-first) column order.
    const base = new Date(2023, 0, 1); // a Sunday
    return Array.from({ length: 7 }, (_, i) => new Date(base.getTime() + i * DAY_MS).toLocaleDateString(undefined, { weekday: 'short' }));
  }, []);

  const summaryLabel = useMemo(() => {
    if (preset !== 'custom') return t(`reports.datePreset.${preset}` as any);
    if (!startDate || !endDate) return t('reports.datePreset.custom');
    const fmt = (d: Date) => d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    return sameDay(startDate, endDate) ? fmt(startDate) : `${fmt(startDate)} – ${fmt(endDate)}`;
  }, [preset, startDate, endDate, t]);

  const isInPendingRange = (day: Date): boolean => {
    if (!pendingStart) return false;
    const end = pendingEnd || pendingStart;
    return day.getTime() >= startOfDay(pendingStart).getTime() && day.getTime() <= endOfDay(end).getTime();
  };

  return (
    <>
      <TouchableOpacity style={[styles.trigger, { flexDirection: rtl.rowDirection }]} onPress={open}>
        <Ionicons name="calendar-outline" size={14} color={colors.text.secondary as string} />
        <Text style={styles.triggerText} numberOfLines={1}>{summaryLabel}</Text>
        <Ionicons name="chevron-down" size={14} color={colors.text.secondary as string} />
      </TouchableOpacity>

      <Modal visible={visible} transparent animationType="fade" onRequestClose={() => setVisible(false)}>
        <TouchableOpacity style={styles.overlay} activeOpacity={1} onPress={() => setVisible(false)}>
          <TouchableOpacity activeOpacity={1} style={styles.card} onPress={() => {}}>
            <View style={[styles.presetRow, { flexDirection: rtl.rowDirection }]}>
              {(['today', '7d', '30d'] as const).map((p) => (
                <TouchableOpacity key={p} style={[styles.presetChip, preset === p && styles.presetChipActive]} onPress={() => applyPreset(p)}>
                  <Text style={[styles.presetChipText, preset === p && styles.presetChipTextActive]}>{t(`reports.datePreset.${p}` as any)}</Text>
                </TouchableOpacity>
              ))}
              <View style={[styles.presetChip, preset === 'custom' && styles.presetChipActive]}>
                <Text style={[styles.presetChipText, preset === 'custom' && styles.presetChipTextActive]}>{t('reports.datePreset.custom')}</Text>
              </View>
            </View>

            <View style={[styles.monthNavRow, { flexDirection: rtl.rowDirection }]}>
              <TouchableOpacity onPress={() => setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, 1))} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
                <Ionicons name={rtl.mirrorIcon('chevron-back') as any} size={18} color={colors.text.primary as string} />
              </TouchableOpacity>
              <Text style={styles.monthLabel}>{monthLabel}</Text>
              <TouchableOpacity onPress={() => setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1))} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
                <Ionicons name={rtl.mirrorIcon('chevron-forward') as any} size={18} color={colors.text.primary as string} />
              </TouchableOpacity>
            </View>

            <View style={styles.weekdayRow}>
              {weekdayLabels.map((w) => (
                <Text key={w} style={styles.weekdayText}>{w}</Text>
              ))}
            </View>

            <View style={styles.grid}>
              {grid.map((day, i) => {
                if (!day) return <View key={`empty-${i}`} style={styles.dayCell} />;
                const isStart = sameDay(day, pendingStart);
                const isEnd = sameDay(day, pendingEnd);
                const inRange = isInPendingRange(day);
                const isToday = sameDay(day, new Date());
                return (
                  <TouchableOpacity
                    key={day.getTime()}
                    style={[styles.dayCell, inRange && styles.dayCellInRange, (isStart || isEnd) && styles.dayCellEdge]}
                    onPress={() => handleDayPress(day)}
                  >
                    <Text style={[styles.dayText, (isStart || isEnd) && styles.dayTextEdge, isToday && !isStart && !isEnd && styles.dayTextToday]}>
                      {day.getDate()}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </View>

            <View style={[styles.footerRow, { flexDirection: rtl.rowDirection }]}>
              <TouchableOpacity style={styles.footerBtnSecondary} onPress={() => setVisible(false)}>
                <Text style={styles.footerBtnSecondaryText}>{t('common.cancel')}</Text>
              </TouchableOpacity>
              <TouchableOpacity style={[styles.footerBtnPrimary, !pendingStart && styles.footerBtnDisabled]} onPress={confirmCustom} disabled={!pendingStart}>
                <Text style={styles.footerBtnPrimaryText}>{t('common.apply')}</Text>
              </TouchableOpacity>
            </View>
          </TouchableOpacity>
        </TouchableOpacity>
      </Modal>
    </>
  );
};

const getStyles = () => StyleSheet.create({
  trigger: {
    alignItems: 'center',
    gap: spacing.xs,
    alignSelf: 'flex-start',
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: 8,
    backgroundColor: colors.background.secondary as string,
  },
  triggerText: {
    fontSize: typography.fontSize.sm,
    color: colors.text.primary as string,
  },
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.4)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  card: {
    width: '90%',
    maxWidth: 360,
    backgroundColor: colors.background.surface as string,
    borderRadius: 14,
    padding: spacing.lg,
    gap: spacing.sm,
  },
  presetRow: {
    gap: spacing.xs,
    flexWrap: 'wrap',
  },
  presetChip: {
    paddingHorizontal: spacing.sm,
    paddingVertical: 6,
    borderRadius: 8,
    backgroundColor: colors.background.secondary as string,
  },
  presetChipActive: {
    backgroundColor: withAlpha(colors.primary, 0.13),
  },
  presetChipText: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary as string,
  },
  presetChipTextActive: {
    color: colors.primary as string,
    fontWeight: '600' as const,
  },
  monthNavRow: {
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: spacing.sm,
  },
  monthLabel: {
    fontSize: typography.fontSize.base,
    fontWeight: '600' as const,
    color: colors.text.primary as string,
  },
  weekdayRow: {
    flexDirection: 'row',
    marginTop: spacing.xs,
  },
  weekdayText: {
    width: `${100 / 7}%`,
    textAlign: 'center',
    fontSize: 11,
    color: colors.text.disabled as string,
  },
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  dayCell: {
    width: `${100 / 7}%`,
    aspectRatio: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  dayCellInRange: {
    backgroundColor: withAlpha(colors.primary, 0.08),
  },
  dayCellEdge: {
    backgroundColor: colors.primary as string,
    borderRadius: 999,
  },
  dayText: {
    fontSize: typography.fontSize.sm,
    color: colors.text.primary as string,
  },
  dayTextEdge: {
    color: '#FFFFFF',
    fontWeight: '700' as const,
  },
  dayTextToday: {
    color: colors.primary as string,
    fontWeight: '700' as const,
  },
  footerRow: {
    justifyContent: 'flex-end',
    gap: spacing.sm,
    marginTop: spacing.sm,
  },
  footerBtnSecondary: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: 10,
    backgroundColor: colors.background.secondary as string,
  },
  footerBtnSecondaryText: {
    color: colors.text.primary as string,
    fontWeight: '600' as const,
  },
  footerBtnPrimary: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: 10,
    backgroundColor: colors.primary as string,
  },
  footerBtnDisabled: {
    opacity: 0.5,
  },
  footerBtnPrimaryText: {
    color: '#FFFFFF',
    fontWeight: '700' as const,
  },
});
